import LanguageIcon from '@mui/icons-material/Language';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { Stack, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';

export type TimeDisplayMode = 'local' | 'utc';

interface TimeDisplayToggleProps {
  value: TimeDisplayMode;
  onChange: (mode: TimeDisplayMode) => void;
  size?: 'small' | 'medium' | 'large';
}

/**
 * Reusable toggle component for switching between local and UTC time display
 *
 * Used in schedule grids and other time-sensitive components to allow users
 * to view times in their local timezone or UTC.
 *
 * @example
 * const [timeMode, setTimeMode] = useState<TimeDisplayMode>('local');
 * <TimeDisplayToggle value={timeMode} onChange={setTimeMode} />
 */
export const TimeDisplayToggle = ({ value, onChange, size = 'small' }: TimeDisplayToggleProps) => {
  return (
    <ToggleButtonGroup
      value={value}
      exclusive
      onChange={(_, newValue) => {
        if (newValue !== null) {
          onChange(newValue);
        }
      }}
      size={size}
    >
      <ToggleButton value="local">
        <Tooltip title="Show times in your local timezone">
          <Stack direction="row" spacing={0.5} alignItems="center">
            <ScheduleIcon fontSize="small" />
            <Typography variant="caption">Local</Typography>
          </Stack>
        </Tooltip>
      </ToggleButton>
      <ToggleButton value="utc">
        <Tooltip title="Show times in UTC">
          <Stack direction="row" spacing={0.5} alignItems="center">
            <LanguageIcon fontSize="small" />
            <Typography variant="caption">UTC</Typography>
          </Stack>
        </Tooltip>
      </ToggleButton>
    </ToggleButtonGroup>
  );
};
